import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Text,
  Flex,
  Button,
  IconButton,
  Input,
  Select,
  HStack,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiSearch, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

export interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  isNumeric?: boolean;
  sortable?: boolean;
  sortKey?: string; // Optional custom sort key for API
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  keyField: keyof T;
  totalItems?: number;
  currentPage?: number;
  pageSize?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSearch?: (query: string) => void;
  onSort?: (sort_by: string, sort_direction: 'asc' | 'desc') => void;
  actionButtons?: (row: T) => React.ReactNode;
}

// Helper function to check deep equality of objects
function isEqual<T>(prev: T, next: T): boolean {
  if (prev === next) return true;
  
  if (
    prev === null || 
    next === null || 
    typeof prev !== 'object' || 
    typeof next !== 'object'
  ) {
    return prev === next;
  }
  
  // Handle arrays
  if (Array.isArray(prev) && Array.isArray(next)) {
    if (prev.length !== next.length) return false;
    return prev.every((value, index) => isEqual(value, next[index]));
  }
  
  const keysA = Object.keys(prev);
  const keysB = Object.keys(next);
  
  if (keysA.length !== keysB.length) return false;
  
  return keysA.every(key => {
    return (
      Object.prototype.hasOwnProperty.call(next, key) && 
      isEqual(prev[key as keyof T], next[key as keyof T])
    );
  });
}

// Custom comparison function for memoization
function arePropsEqual<T>(
  prevProps: DataTableProps<T>, 
  nextProps: DataTableProps<T>
): boolean {
  // Compare primitive props first
  if (
    prevProps.isLoading !== nextProps.isLoading ||
    prevProps.totalItems !== nextProps.totalItems ||
    prevProps.currentPage !== nextProps.currentPage ||
    prevProps.pageSize !== nextProps.pageSize ||
    prevProps.keyField !== nextProps.keyField ||
    prevProps.sort_by !== nextProps.sort_by ||
    prevProps.sort_direction !== nextProps.sort_direction
  ) {
    return false;
  }
  
  // Compare column definitions
  if (!isEqual(prevProps.columns, nextProps.columns)) {
    return false;
  }
  
  // Compare data array
  if (!isEqual(prevProps.data, nextProps.data)) {
    return false;
  }
  
  // Compare callback references
  if (
    prevProps.onPageChange !== nextProps.onPageChange ||
    prevProps.onPageSizeChange !== nextProps.onPageSizeChange ||
    prevProps.onSearch !== nextProps.onSearch ||
    prevProps.onSort !== nextProps.onSort ||
    prevProps.actionButtons !== nextProps.actionButtons
  ) {
    return false;
  }
  
  return true;
}

const DataTableInner = <T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  keyField,
  totalItems = 0,
  currentPage = 1,
  pageSize = 10,
  sort_by,
  sort_direction,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onSort,
  actionButtons,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = React.useState('');
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleSearch = React.useCallback(() => {
    if (onSearch) {
      onSearch(searchQuery);
    }
  }, [onSearch, searchQuery]);

  const handleSort = React.useCallback((column: Column<T>) => {
    if (!onSort || !column.sortable) return;
    
    const sortKey = column.sortKey || (typeof column.accessor === 'string' ? column.accessor : '');
    if (!sortKey) return;
    
    let newDirection: 'asc' | 'desc' = 'asc';
    if (sort_by === sortKey && sort_direction === 'asc') {
      newDirection = 'desc';
    }
    
    onSort(sortKey, newDirection);
  }, [onSort, sort_by, sort_direction]);

  const getSortIcon = React.useCallback((column: Column<T>) => {
    if (!column.sortable) return null;
    
    const sortKey = column.sortKey || (typeof column.accessor === 'string' ? column.accessor : '');
    if (sort_by !== sortKey) return null;
    
    return sort_direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />;
  }, [sort_by, sort_direction]);

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      borderColor={borderColor}
      bg={bg}
    >
      {onSearch && (
        <Flex p={4} borderBottomWidth="1px" borderColor={borderColor}>
          <HStack width="full">
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <IconButton
              aria-label="Search"
              icon={<FiSearch />}
              onClick={handleSearch}
            />
          </HStack>
        </Flex>
      )}

      <Box overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              {columns.map((column, index) => (
                <Th
                  key={index}
                  isNumeric={column.isNumeric}
                  py={4}
                  fontSize="sm"
                  fontWeight="semibold"
                  cursor={column.sortable ? 'pointer' : 'default'}
                  onClick={() => handleSort(column)}
                  _hover={column.sortable ? { bg: useColorModeValue('gray.50', 'gray.700') } : undefined}
                >
                  <HStack spacing={1}>
                    <Text>{column.header}</Text>
                    {getSortIcon(column)}
                  </HStack>
                </Th>
              ))}
              {actionButtons && <Th>Actions</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={columns.length + (actionButtons ? 1 : 0)} textAlign="center" py={4}>
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length + (actionButtons ? 1 : 0)} textAlign="center" py={4}>
                  <Text>{t('common.noData')}</Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => (
                <Tr key={String(row[keyField])}>
                  {columns.map((column, index) => (
                    <Td key={index} isNumeric={column.isNumeric} pl={6} py={2} fontSize={14}>
                      {typeof column.accessor === 'function'
                        ? column.accessor(row)
                        : row[column.accessor]}
                    </Td>
                  ))}
                  {actionButtons && (
                    <Td>
                      <HStack spacing={1}>{actionButtons(row)}</HStack>
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {(onPageChange || onPageSizeChange) && (
        <Flex
          justifyContent="space-between"
          p={2}
          alignItems="center"
          borderTopWidth="1px"
          borderColor={borderColor}
        >
          {onPageSizeChange && (
            <HStack>
              <Text fontSize="sm">Show</Text>
              <Select
                size="sm"
                value={pageSize}
                onChange={(e) =>
                  onPageSizeChange(parseInt(e.target.value, 10))
                }
                maxW="100px"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </Select>
              <Text fontSize="sm">entries</Text>
            </HStack>
          )}

          {onPageChange && totalPages > 1 && (
            <HStack>
              <IconButton
                aria-label="Previous page"
                icon={<FiChevronLeft />}
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                size="sm"
              />
              <Text fontSize="sm">
                Page {currentPage} of {totalPages}
              </Text>
              <IconButton
                aria-label="Next page"
                icon={<FiChevronRight />}
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                size="sm"
              />
            </HStack>
          )}
        </Flex>
      )}
    </Box>
  );
};

const DataTable = React.memo(DataTableInner, arePropsEqual) as typeof DataTableInner;

export default DataTable; 